import React, { useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
    NavigationMenu,
    NavigationMenuItem,
    NavigationMenuLink,
    NavigationMenuList,
    navigationMenuTriggerStyle,
} from '@/components/ui/navigation-menu';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
    SheetClose,
    SheetDescription,
} from "@/components/ui/sheet";
import { Menu } from 'lucide-react';
import { useSupabaseAuth } from '@/lib/hooks';
import { cn } from '@/utils/cn';

type NavItem = {
    to: string;
    label: string;
    adminOnly?: boolean;
};

const NAV_ITEMS: NavItem[] = [
    { to: '/dashboard', label: '進行中案件' },
    { to: '/projects', label: '案件一覧', adminOnly: true },
    { to: '/masters', label: 'マスタ管理', adminOnly: true },
    { to: '/materials', label: '資材在庫管理', adminOnly: true },
    { to: '/users', label: 'ユーザー管理', adminOnly: true },
    { to: '/reports', label: '月次稼働サマリ', adminOnly: true },
];

export const Header = React.memo(() => {
    const { user, isLoading, isAdmin, signOut } = useSupabaseAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const displayName = user?.name || user?.user_metadata?.name || user?.email;

    const handleLogout = async () => {
        try {
            await signOut();
            navigate('/', { replace: true, flushSync: true });
        } catch (error) {
            console.error('ログアウト処理でエラーが発生しました:', error);
        }
    };

    const visibleNavItems = useMemo(() => {
        return NAV_ITEMS.filter((item) => !item.adminOnly || isAdmin);
    }, [isAdmin]);

    const isActivePath = (targetPath: string) => {
        if (targetPath === '/dashboard') {
            return location.pathname === '/dashboard';
        }
        return location.pathname === targetPath || location.pathname.startsWith(`${targetPath}/`);
    };

    const desktopNavBaseClasses = `${navigationMenuTriggerStyle()} bg-transparent px-2 py-2 text-sm font-medium whitespace-nowrap tracking-[0.02em] text-[hsl(var(--header-foreground))] hover:bg-white/10 hover:text-[hsl(var(--header-foreground))] focus:bg-white/10 focus:text-[hsl(var(--header-foreground))]`;
    const mobileNavBaseClasses = 'block w-full rounded-md px-3 py-2 text-left text-sm font-medium transition-colors text-foreground hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground';

    const homePath = '/dashboard';

    return (
        <header className="sticky top-0 z-[var(--z-header)] border-b border-white/10 bg-[hsl(var(--header-bg))] text-[hsl(var(--header-foreground))]" role="banner">
            <a
                href="#main-content"
                className="sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:z-50 focus:rounded-md focus:bg-background focus:px-3 focus:py-2 focus:text-foreground"
            >
                メインコンテンツへスキップ
            </a>
            <div className="container mx-auto flex min-h-[56px] max-w-screen-2xl items-center justify-between px-3 py-2 sm:min-h-[48px] sm:px-5 lg:px-8">
                <Link
                    to={homePath}
                    className="whitespace-nowrap text-lg font-semibold tracking-tight transition-colors hover:text-white/85 sm:text-xl"
                    aria-label="genbadock ホーム"
                >
                    genbadock
                </Link>

                {/* Desktop Navigation */}
                <NavigationMenu className="mx-4 hidden flex-1 justify-center lg:flex" role="navigation" aria-label="メインナビゲーション">
                    <NavigationMenuList className="flex-wrap justify-center">
                        {visibleNavItems.map((item) => (
                            <NavigationMenuItem key={item.to}>
                                <NavigationMenuLink asChild>
                                    <Link
                                        to={item.to}
                                        aria-current={isActivePath(item.to) ? 'page' : undefined}
                                        className={cn(
                                            desktopNavBaseClasses,
                                            isActivePath(item.to) && 'rounded-md bg-white/12 text-[hsl(var(--header-foreground))]'
                                        )}
                                    >
                                        {item.label}
                                    </Link>
                                </NavigationMenuLink>
                            </NavigationMenuItem>
                        ))}
                    </NavigationMenuList>
                </NavigationMenu>

                {/* User Info / Logout Button (Desktop) */}
                <div className="hidden min-w-[12rem] flex-shrink-0 items-center justify-end space-x-2 lg:flex">
                    {isLoading ? (
                        <span className="text-sm text-muted-foreground" aria-live="polite">認証状態を確認中…</span>
                    ) : user ? (
                        <>
                            <span className="max-w-32 truncate text-sm text-white/75">{displayName}</span>
                            <Button variant="ghost" size="sm" onClick={handleLogout} className="min-h-11 px-2 text-sm text-white hover:bg-white/10 hover:text-white">
                                ログアウト
                            </Button>
                        </>
                    ) : (
                        <Link to="/login">
                            <Button variant="outline" size="sm" className="min-h-11 border-white/30 bg-transparent px-2 text-sm text-white hover:bg-white/10 hover:text-white">ログイン</Button>
                        </Link>
                    )}
                </div>

                {/* Mobile/Tablet Navigation Button (Hamburger) */}
                <div className="lg:hidden">
                    <Sheet>
                        <SheetTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-11 w-11 hover:bg-white/10 focus:bg-white/10 active:bg-white/15" aria-label="メニューを開く">
                                <Menu className="h-6 w-6 text-[hsl(var(--header-foreground))]" />
                                <span className="sr-only">メニューを開閉</span>
                            </Button>
                        </SheetTrigger>
                        <SheetContent side="left" className="bg-background text-foreground border-border pt-10">
                            <SheetHeader className="mb-6">
                                <SheetTitle className="text-center text-base">ナビゲーション</SheetTitle>
                                <SheetDescription className="text-muted-foreground text-center text-sm">
                                    アプリケーションのメインメニューです
                                </SheetDescription>
                            </SheetHeader>
                            <nav className="flex flex-col space-y-1" role="navigation" aria-label="モバイルナビゲーション">
                                <div className="flex flex-col space-y-1">
                                    {visibleNavItems.map((item) => (
                                        <SheetClose asChild key={item.to}>
                                            <Link
                                                to={item.to}
                                                aria-current={isActivePath(item.to) ? 'page' : undefined}
                                                className={cn(mobileNavBaseClasses, isActivePath(item.to) && 'bg-accent text-accent-foreground font-semibold')}
                                            >
                                                {item.label}
                                            </Link>
                                        </SheetClose>
                                    ))}
                                </div>

                                <div className="mt-5 border-t border-border/70 pt-5">
                                {isLoading ? (
                                    <span className="px-3 py-2 text-sm text-muted-foreground" aria-live="polite">認証状態を確認中…</span>
                                ) : user ? (
                                    <div className="flex flex-col space-y-3 items-start px-2">
                                        <span className="text-sm font-medium px-1 py-2">{displayName}</span>
                                        <SheetClose asChild>
                                            <Button variant="ghost" className="w-full justify-start text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground" onClick={handleLogout}>
                                                ログアウト
                                            </Button>
                                        </SheetClose>
                                    </div>
                                ) : (
                                    <SheetClose asChild>
                                        <Link to="/login" className='w-full px-2'>
                                            <Button variant="outline" className="w-full justify-start text-sm">
                                                ログイン
                                            </Button>
                                        </Link>
                                    </SheetClose>
                                )}
                                </div>
                            </nav>
                        </SheetContent>
                    </Sheet>
                </div>
            </div>
        </header>
    );
});

Header.displayName = 'Header'; 
